import { useCallback, useState } from "react";
import { classNames } from "../../utils";
import { FolderPlusIcon as FolderAddIcon } from "@heroicons/react/24/outline";
import { useDropzone } from "react-dropzone";
import Button, { ButtonColorTypes } from "../../shared-components/Button";

type Props = {
  files: File[] | undefined;
  onChange: (files: File[] | undefined) => void;
  initialLogoURL?: string;
  isReadOnly?: boolean;
};

const LogoUploadDropZone = ({
  files,
  initialLogoURL,
  onChange,
  isReadOnly = true,
}: Props) => {
  const [changeLogo, setChangeLogo] = useState(initialLogoURL ? false : true);
  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles || acceptedFiles.length === 0) {
        setChangeLogo(true);
        onChange(undefined);
        return;
      }
      onChange(acceptedFiles);
      setChangeLogo(false);
    },
    [onChange]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    maxSize: 300000,
    onError: (err) => {
      console.error("Error uploading logo", err);
    },
    onDropRejected(fileRejections, event) {
      console.error("Rejected files", fileRejections);
    },
  });

  function getLogoURL() {
    if (initialLogoURL || files) {
      let logoURL = "";
      if (files) {
        logoURL = URL.createObjectURL(files[0]);
      } else {
        logoURL = `data:image/png;base64,${initialLogoURL}`;
      }
      return logoURL;
    }
    return "";
  }

  const showUploadedImage = !!getLogoURL() && !changeLogo;
  return (
    <>
      <div className="flex justify-between items-center">
        <span className="text-sm font-semibold text-gray-500">Logo Upload</span>
      </div>

      {showUploadedImage && (
        <div>
          <img src={getLogoURL()} alt="logo" className="w-32 py-4" />
          <Button
            text={"Change logo"}
            onClick={() => setChangeLogo(true)}
            disabled={isReadOnly}
            colorType={ButtonColorTypes.outline}
          />
        </div>
      )}

      <div
        {...getRootProps()}
        className={classNames(
          "w-full rounded-lg border-2 p-4 my-4 border-dashed border-gray-300",
          !changeLogo ? "hidden" : "",
          "bg-white"
        )}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col  justify-center items-center text-gray-300 p-6">
          <span>
            <FolderAddIcon className="w-12" />
          </span>
          <span className="my-2">
            <Button
              text={"Choose file to upload"}
              disabled={isReadOnly}
              colorType={ButtonColorTypes.outline}
            />
          </span>
          <span>or drag and drop them here</span>
          <span className="text-xs text-center font-semibold">
            Please ensure your image is in PNG or JPG format and has a width of
            579px and a height of 160px
          </span>
        </div>
      </div>
    </>
  );
};

export default LogoUploadDropZone;
